import { useEffect } from 'react';

import { useLanguageContext } from '../components/ui/LanguageProvider';

export const useDirection = () => {
  const { currentLanguage, isRTL } = useLanguageContext();

  useEffect(() => {
    // Update document direction and lang attributes
    document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
    document.documentElement.lang = currentLanguage;
    
    // Update body class for CSS targeting
    document.body?.classList?.remove('rtl', 'ltr');
    document.body?.classList?.add(isRTL ? 'rtl' : 'ltr');
    
    // Update meta charset for proper rendering
    let metaCharset = document.querySelector('meta[charset]');
    if (!metaCharset) {
      metaCharset = document.createElement('meta');
      metaCharset.setAttribute('charset', 'UTF-8');
      document.head?.insertBefore(metaCharset, document.head.firstChild);
    }
    
    // Update or add dir meta tag for better RTL support
    let metaDir = document.querySelector('meta[name="dir"]');
    if (!metaDir) {
      metaDir = document.createElement('meta');
      metaDir.setAttribute('name', 'dir');
      document.head?.appendChild(metaDir);
    }
    metaDir.setAttribute('content', isRTL ? 'rtl' : 'ltr');
    
    // Update title direction if needed
    const title = document.title;
    if (title && isRTL) {
      // Ensure proper RTL display in browser tab
      document.title = title;
    }
  }, [currentLanguage, isRTL]);

  // Helper functions for directional classes
  const getTextAlign = (alignment = 'start') => {
    if (alignment === 'start') {
      return isRTL ? 'text-right' : 'text-left';
    } else if (alignment === 'end') {
      return isRTL ? 'text-left' : 'text-right';
    }
    return `text-${alignment}`;
  };

  const getFlexDirection = (reverse = false) => {
    if (reverse) {
      return isRTL ? 'flex-row' : 'flex-row-reverse';
    }
    return isRTL ? 'flex-row-reverse' : 'flex-row';
  };

  const getMarginClass = (direction, size) => {
    const directionMap = {
      'start': isRTL ? 'mr' : 'ml',
      'end': isRTL ? 'ml' : 'mr',
      'left': isRTL ? 'mr' : 'ml',
      'right': isRTL ? 'ml' : 'mr'
    };
    
    const finalDirection = directionMap[direction] || direction;
    return `m${finalDirection}-${size}`;
  };

  const getPaddingClass = (direction, size) => {
    const directionMap = {
      'start': isRTL ? 'pr' : 'pl',
      'end': isRTL ? 'pl' : 'pr', 
      'left': isRTL ? 'pr' : 'pl',
      'right': isRTL ? 'pl' : 'pr'
    };
    
    const finalDirection = directionMap[direction] || direction;
    return `p${finalDirection}-${size}`;
  };

  const getDirectionClass = () => {
    return isRTL ? 'rtl' : 'ltr';
  };

  const getBorderClass = (direction, size = '1') => {
    const directionMap = {
      'start': isRTL ? 'border-r' : 'border-l',
      'end': isRTL ? 'border-l' : 'border-r',
      'left': isRTL ? 'border-r' : 'border-l', 
      'right': isRTL ? 'border-l' : 'border-r'
    };
    
    const finalDirection = directionMap[direction] || `border-${direction}`;
    return size === '1' ? finalDirection : `${finalDirection}-${size}`;
  };

  const getJustifyContent = (alignment = 'start') => {
    if (alignment === 'start') {
      return isRTL ? 'justify-end' : 'justify-start';
    } else if (alignment === 'end') {
      return isRTL ? 'justify-start' : 'justify-end';
    }
    return `justify-${alignment}`;
  };

  const getItemsAlignment = (alignment = 'start') => {
    if (alignment === 'start') {
      return isRTL ? 'items-end' : 'items-start';
    } else if (alignment === 'end') {
      return isRTL ? 'items-start' : 'items-end';
    }
    return `items-${alignment}`;
  };

  return {
    currentLanguage,
    isRTL,
    direction: isRTL ? 'rtl' : 'ltr',
    getTextAlign,
    getFlexDirection,
    getMarginClass,
    getPaddingClass,
    getDirectionClass,
    getBorderClass,
    getJustifyContent,
    getItemsAlignment
  };
};

export default useDirection;